/**
 * IFrame Application for Lobby2.0
 * Handles mounting the lobby inside the Game Client via iframe
 * Replicates the old lobby's IFrameApplication functionality
 */

import { Subject } from '../core/utils/Subject';
import type { IExternalChannel } from '../types/external/IExternalChannel';
import type { IOptions } from '../types/external/IOptions';
import HistoryManagerService from '../core/services/managers/HistoryManager';
import { FULLSCREEN_OPEN_TYPE, INGAME_OPEN_TYPE, MOBILE_MODE } from '../constants';
import { externalCommunication } from '../core/services/communication/ExternalCommunication';

const LOBBY_INGAME_LEFT_RIGHT_OFFSET = '8px';
const LOBBY_INGAME_TOP_BOTTOM_OFFSET = '8px';

const INGAME_CONTAINER_HEIGHT = `calc(var(--lobby-container-height) - ${LOBBY_INGAME_TOP_BOTTOM_OFFSET} * 2)`;
const INGAME_CONTAINER_WIDTH = `calc(var(--lobby-container-width) - ${LOBBY_INGAME_LEFT_RIGHT_OFFSET} * 2)`;
const INGAME_CONTAINER_TOP = `calc(50% - var(--lobby-container-height) / 2 + ${LOBBY_INGAME_TOP_BOTTOM_OFFSET})`;
const INGAME_CONTAINER_LEFT = `calc(50% - var(--lobby-container-width) / 2 + ${LOBBY_INGAME_LEFT_RIGHT_OFFSET})`;

export default class IFrameApplication {
  private mountingElement: HTMLElement;
  private comeFromLobby: number = 0;
  private iframe: HTMLIFrameElement | null = null;
  private historyManager: HistoryManagerService;
  private settings: IOptions;

  /**
   * External communication channel for Game Client communication
   */
  public EXTERNAL_CHANNEL = new Subject<IExternalChannel>();

  constructor(settings: IOptions, elementId: string = 'react-lobby-remote-root') {
    this.settings = settings;

    // Use existing element if Client created it, otherwise create one
    this.mountingElement = document.getElementById(elementId) || IFrameApplication.getElement(elementId);

    // Initialize history manager (like old lobby)
    this.historyManager = new HistoryManagerService({
      closeUrl: this.settings.closeUrl,
      isAggregator: this.settings.isAggregator,
      gameKey: this.settings.gameKey,
    });

    this.loadUrlParams();
    this.applyElementStyles();
    this.listenForLobbyAppMessages();
    this.listenForGameClientMessages();
  }

  public static getElement(name: string): HTMLElement {
    let element = document.getElementById(name);
    if (!element) {
      element = document.createElement('div');
      element.setAttribute('id', name);
      document.body.appendChild(element);
    }
    return element;
  }

  public get channel(): Subject<IExternalChannel> {
    return this.EXTERNAL_CHANNEL;
  }

  public get element(): HTMLElement {
    return this.mountingElement;
  }

  public renderIframeApplication(): void {
    const iframeUrl = `${this.settings.host}/?${this.encodeParams()}`;

    this.createBgBlocker();
    this.createIframe(iframeUrl);
    this.element.appendChild(this.iframe as HTMLIFrameElement);

    // Monitor and fix iframe sizing
    this.monitorIframeSizing();
  }

  private monitorIframeSizing(): void {
    // Check iframe size immediately and after a short delay
    const checkSizing = () => {
      if (this.iframe) {
        const rect = this.iframe.getBoundingClientRect();

        // If iframe is too small, force it again to cover fully its container
        if (rect.width < window.innerWidth * 0.9 || rect.height < window.innerHeight * 0.9) {
          const ingame = this.settings.openType === INGAME_OPEN_TYPE;
          this.iframe.style.cssText = this.getIframeCssText(ingame);
        }
      }
    };

    // Check immediately and after DOM updates
    checkSizing();
    setTimeout(checkSizing, 100);
    setTimeout(checkSizing, 500);
    setTimeout(checkSizing, 1000);
  }

  private createIframe(src: string): void {
    this.iframe = document.createElement('iframe') as HTMLIFrameElement;
    this.iframe.setAttribute('allow', 'autoplay');

    // Apply initial sizing according to current openType
    // Note: color-scheme is set in getIframeCssText() for Samsung dark mode defense
    this.applyIframeSizing();

    this.iframe.src = src;
  }

  private applyIframeSizing(): void {
    if (!this.iframe) return;
    const ingame = this.settings.openType === INGAME_OPEN_TYPE;
    this.iframe.style.cssText = this.getIframeCssText(ingame);
  }

  private getIframeCssText(ingame: boolean): string {
    return `
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      border: none;
      ${ingame ? 'border: 1px solid #c9c6c6; border-radius: 10px;' : ''}
      display: block;
      color-scheme: light only !important;
    `;
  }

  private applyContainerFullscreenStyles(): void {
    this.mountingElement.style.setProperty('position', 'fixed');
    this.mountingElement.style.setProperty('top', '0');
    this.mountingElement.style.setProperty('left', '0');
    this.mountingElement.style.setProperty('border', 'none');
    this.mountingElement.style.setProperty('width', '100vw');
    this.mountingElement.style.setProperty('height', '100dvh');
    this.mountingElement.style.setProperty('overflow', 'hidden');
    this.mountingElement.style.setProperty('box-sizing', 'border-box');
  }

  private applyContainerIngameStyles(): void {
    this.mountingElement.style.setProperty('position', 'fixed');
    this.mountingElement.style.setProperty('border', 'none');
    this.mountingElement.style.setProperty('overflow', 'hidden');
    this.mountingElement.style.setProperty('box-sizing', 'border-box');

    // On desktop, attach to viewport edges with small offsets to avoid floating centered modal look
    if (this.settings.mode === 'desktop') {
      this.mountingElement.style.setProperty('top', LOBBY_INGAME_TOP_BOTTOM_OFFSET);
      this.mountingElement.style.setProperty('left', LOBBY_INGAME_LEFT_RIGHT_OFFSET);
      this.mountingElement.style.setProperty('width', `calc(100vw - ${LOBBY_INGAME_LEFT_RIGHT_OFFSET} * 2)`); 
      this.mountingElement.style.setProperty('height', `calc(100dvh - ${LOBBY_INGAME_TOP_BOTTOM_OFFSET} * 2)`);
    } else {
      // On mobile, keep centered calculation based on provided container vars
      this.mountingElement.style.setProperty('top', INGAME_CONTAINER_TOP);
      this.mountingElement.style.setProperty('left', INGAME_CONTAINER_LEFT);
      this.mountingElement.style.setProperty('width', INGAME_CONTAINER_WIDTH);
      this.mountingElement.style.setProperty('height', INGAME_CONTAINER_HEIGHT);
    }
  }

  private setContainerInlineStyles(openType: 'fullscreen' | 'ingame'): void {
    // Aggressively force the mount element to be with desired dimensions
    if (openType === FULLSCREEN_OPEN_TYPE) {
      this.applyContainerFullscreenStyles();
    }
    // Ingame
    else {
      this.applyContainerIngameStyles();
    }
  }

  private updateElementClass(addClassName: string, removeClassName: string): void {
    if (this.mountingElement.classList.contains(removeClassName)) {
      this.mountingElement.classList.replace(removeClassName, addClassName);
    } else {
      this.mountingElement.classList.add(addClassName);
    }
  }

  private applyElementStyles(): void {
    // Apply CSS classes based on openType and mode (like old lobby)
    if (!this.mountingElement || !this.settings.openType || !this.settings.mode) {
      return;
    }

    // When the iframe is mounted the default value of openType is taken - which is 'fullscreen',
    // the real openType value we recieve when the user clicks on the lobby button
    this.setContainerInlineStyles(this.settings.openType);

    // Handle openType classes
    this.updateElementClass(
      this.settings.openType === FULLSCREEN_OPEN_TYPE ? 'egt_react_lobby_fullscreen' : 'egt_react_lobby_ingame',
      this.settings.openType === FULLSCREEN_OPEN_TYPE ? 'egt_react_lobby_ingame' : 'egt_react_lobby_fullscreen'
    );

    // Handle device mode classes
    this.updateElementClass(
      this.settings.mode === MOBILE_MODE ? 'egt_react_lobby_mobile' : 'egt_react_lobby_desktop',
      this.settings.mode === MOBILE_MODE ? 'egt_react_lobby_desktop' : 'egt_react_lobby_mobile'
    );

  }

  private messageHandler = (event: MessageEvent) => {
    if (event.source === this.iframe?.contentWindow) {
      if (event.data.requestType && event.data.requestType === 'incoming') {
        return;
      }


      if (event.data.event === 'startLobbyGame') {
        // Handle game start using history manager (like old lobby)
        this.historyManager?.onStartNewGame(event.data.value);
      } else if (event.data.event === 'settingsModalToggle') {
        // Handle settings modal scrolling
        this.iframe?.setAttribute('scrolling', `${event.data.value ? 'no' : 'yes'}`);
      } else if (event.data.command === 'exit') {
        // Handle exit command using history manager (like old lobby)
        this.historyManager?.onExitApplication();
      } else {
        // Forward all other events to Game Client (like old lobby)
        this.channel.next(event.data);
      }
    }
  };


  private listenForLobbyAppMessages(): void {
    window.addEventListener('message', this.messageHandler);
  }

  private listenForGameClientMessages(): void {
    this.channel.subscribe(data => {
      // @ts-expect-error - requestType isn't part of IExternalChannel; it's a
      // transient tag some messages carry to avoid an echo loop back to the
      // sender, not a real channel field.
      if (data.requestType && data.requestType === 'outgoing') {
        return;
      }


      if (data.event === 'exit') {
        this.historyManager?.onExitApplication();
        return;
      }

      // Notify Game Client when lobby is opened
      if (data.event === 'display' && data.value && this.settings.sendOpenLobbyEvent) {
        externalCommunication.sendOpenLobby();
      }

      // Update settings from Game Client
      if (data.event === 'settings') {
        this.settings = {
          ...this.settings,
          ...(data.value as IOptions),
        };
        this.applyElementStyles();
        this.updateIframeBorderRadius();
        this.applyIframeSizing();
      }

      // Forward all messages to lobby iframe (like old lobby)
      this.iframe?.contentWindow?.postMessage(data, '*');
    });
  }

  private updateIframeBorderRadius(): void {
    if (this.iframe) {
      if (this.settings.openType === INGAME_OPEN_TYPE) {
        this.iframe.style.borderRadius = '10px';
      } else {
        this.iframe.style.borderRadius = '';
      };
    };
  };

  private createBgBlocker(): void {
    // Only create background blocker if it doesn't exist
    if (!this.mountingElement.querySelector('.egtd_background_blocker')) {
      const backgroundBlocker = document.createElement('div');
      backgroundBlocker.classList.add('egtd_background_blocker');
      if (this.settings.openType === FULLSCREEN_OPEN_TYPE) {
        // In fullscreen, cover the viewport
        backgroundBlocker.style.cssText = `
          width: 100vw;
          height: 100dvh;
          position: fixed;
          top: 0px;
          left: 0px;
        `;
      } else {
        // In ingame, only cover the mounting element area so parent remains visible around
        backgroundBlocker.style.cssText = `
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0px;
          left: 0px;
        `;
      }
      this.mountingElement.appendChild(backgroundBlocker);
    }
  }

  /**
   * Encode all lobby options for iframe URL
   */
  private encodeParams(): string {
    const settings = this.settings;

    const params = [
      ['apiIntegrationHost', settings.apiHost],
      ['assetsHost', settings.assetsHost],
      ['tournamentHost', settings.tournamentHost],
      ['prizePopHost', settings.prizePopHost],
      ['rebateHost', settings.rebateHost],
      ['redirectHost', settings.redirectHost],
      ['mode', settings.mode],
      ['currency', settings.currency],
      ['balance', settings.balance],
      ['units', settings.units],
      ['languageCode', settings.languageCode],
      ['openType', settings.openType],
      ['mute', settings.mute],
      ['bgMusicMute', settings.bgMusicMute],
      ['gameKey', settings.gameKey],
      ['playerId', settings.playerId],
      ['sessionKey', settings.sessionKey],
      ['comeFromLobby', this.comeFromLobby],
      ['iniframe', 'true'],
      ['closeUrl', settings.closeUrl],
      ['sendOpenLobbyEvent', settings.sendOpenLobbyEvent],
      ['mountLobby', settings.mountLobby],
      ['isAggregator', settings.isAggregator],
      ['singleView', settings.singleView],
      ['openSection', settings.openSection]
    ]
      // Filter out undefined values and encode properly
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
      .join('&');

    return params;
  }

  private loadUrlParams(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const comeFromLobby = urlParams.get('comeFromLobby');
    if (comeFromLobby) {
      this.comeFromLobby = parseInt(comeFromLobby, 10);
    }
  }

  public cleanup(): void {
    window.removeEventListener('message', this.messageHandler);
    this.historyManager?.cleanup();
    this.EXTERNAL_CHANNEL.complete();
  }
}